import { MoreHorizontal, X } from 'lucide-react'
import type { ComponentType } from 'react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
} from '@/components/ui'
import { cn } from '@/lib/cn'

export interface SelectionAction {
  /** Stable list key. Also what a page uses to tell two actions apart. */
  key: string
  label: string
  /** The icon component itself, e.g. `icon={Plus}` — not an element. */
  icon: ComponentType<{ className?: string }>
  onSelect: () => void
  destructive?: boolean
  disabled?: boolean
  /** Shows a spinner in place of the icon and blocks the action. */
  loading?: boolean
}

export interface SelectionBarProps {
  count: number
  onClear: () => void
  actions: SelectionAction[]
  className?: string
}

/** Past this many, the tail folds into a menu so the bar never wraps on a phone. */
const INLINE_LIMIT = 3

export function SelectionBar({ count, onClear, actions, className }: SelectionBarProps) {
  const overflows = actions.length > INLINE_LIMIT
  const inline = overflows ? actions.slice(0, INLINE_LIMIT - 1) : actions
  const overflow = overflows ? actions.slice(INLINE_LIMIT - 1) : []
  const nothingSelected = count === 0

  return (
    // The wrapper reproduces AppShell's docked stack — player, then tab bar,
    // then the safe area — so the bar lands exactly on top of it at every width
    // without a magic offset. It ignores the pointer; only the bar takes clicks.
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-20 flex flex-col md:left-60',
        className,
      )}
    >
      <div className="px-3 pb-2">
        <div
          role="toolbar"
          aria-label="Selection actions"
          className="pointer-events-auto mx-auto flex w-full max-w-3xl items-center gap-2 rounded-card border border-border bg-surface-raised p-2 shadow-xl"
        >
          <IconButton aria-label="Clear selection" size="sm" onClick={onClear}>
            <X className="h-5 w-5" aria-hidden="true" />
          </IconButton>

          <p aria-live="polite" className="min-w-0 flex-1 truncate text-sm font-medium text-text">
            {count} selected
          </p>

          {inline.map((action) => (
            <Button
              key={action.key}
              size="sm"
              variant={action.destructive ? 'danger' : 'secondary'}
              aria-label={action.label}
              disabled={action.disabled || nothingSelected}
              loading={action.loading}
              onClick={action.onSelect}
            >
              {action.loading ? null : <action.icon className="h-4 w-4" aria-hidden="true" />}
              <span className="hidden sm:inline">{action.label}</span>
            </Button>
          ))}

          {overflow.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton aria-label="More actions" size="sm" variant="secondary">
                  <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top">
                {overflow.map((action) => (
                  <DropdownMenuItem
                    key={action.key}
                    icon={action.icon}
                    destructive={action.destructive}
                    disabled={action.disabled || action.loading || nothingSelected}
                    onSelect={() => action.onSelect()}
                  >
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <div aria-hidden="true" className="h-player" />
      <div aria-hidden="true" className="h-tabbar md:hidden" />
      <div aria-hidden="true" className="safe-bottom" />
    </div>
  )
}
